/**
 * ExamsTab Component
 * Tab for managing appointment complementary exams within the medical consultation page.
 * Supports status/result updates, file upload/download/delete, and debounced auto-save.
 *
 * @version 2025.1.0
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import type {
  AppointmentExam,
  UpdateExamResultData,
} from '../../../types/appointmentExam';
import {
  AppointmentExamStatus,
  AppointmentExamResult,
  APPOINTMENT_EXAM_STATUS_LABELS,
  APPOINTMENT_EXAM_STATUS_COLORS,
  APPOINTMENT_EXAM_RESULT_LABELS,
  APPOINTMENT_EXAM_RESULT_COLORS,
  APPLICATION_LEVEL_LABELS,
  APPLICATION_LEVEL_COLORS,
} from '../../../types/appointmentExam';
import appointmentExamService from '../../../services/appointmentExamService';
import { ActionIconButton } from '../../../components/common';

/**
 * Mexer no arquivo do exame é ação de duas mãos — é o mesmo `qualquer` do `pode()` da rota
 * que apaga o laudo.
 */
const GERIR_ARQUIVO_DO_EXAME = ['appointment.update', 'referral_report.manage'] as const;

// ============================================================================
// TYPES
// ============================================================================

interface ExamsTabProps {
  appointmentId: number;
  appointmentExams: AppointmentExam[];
  onExamsUpdate: (exams: AppointmentExam[]) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const DEBOUNCE_DELAY = 2000;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR');
}

function validateFile(file: File): string | null {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return 'Tipo de arquivo não permitido. Use PDF, JPEG, PNG ou GIF.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'Arquivo muito grande. Tamanho máximo: 10MB.';
  }
  return null;
}

// ============================================================================
// SINGLE EXAM CARD
// ============================================================================

interface ExamCardProps {
  exam: AppointmentExam;
  appointmentId: number;
  onExamUpdated: (updatedExam: AppointmentExam) => void;
}

const ExamCard: React.FC<ExamCardProps> = ({ exam, appointmentId, onExamUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local form state for text fields (debounced)
  const [resultDetails, setResultDetails] = useState(exam.resultDetails ?? '');
  const [observations, setObservations] = useState(exam.observations ?? '');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when exam prop changes from outside (e.g., after immediate save)
  useEffect(() => {
    setResultDetails(exam.resultDetails ?? '');
    setObservations(exam.observations ?? '');
  }, [exam.resultDetails, exam.observations]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // --------------------------------------------------
  // Save helper
  // --------------------------------------------------

  const saveUpdate = useCallback(
    async (data: UpdateExamResultData) => {
      setSaving(true);
      try {
        const updated = await appointmentExamService.updateResult(appointmentId, exam.id, data);
        onExamUpdated(updated);
        enqueueSnackbar('Exame atualizado', { variant: 'success' });
      } catch (error) {
        console.error('Error updating exam:', error);
        enqueueSnackbar('Erro ao atualizar exame', { variant: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [appointmentId, exam.id, onExamUpdated, enqueueSnackbar],
  );

  // Debounced save for text fields
  const debouncedSave = useCallback(
    (data: UpdateExamResultData) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        saveUpdate(data);
      }, DEBOUNCE_DELAY);
    },
    [saveUpdate],
  );

  // --------------------------------------------------
  // Handlers
  // --------------------------------------------------

  const handleStatusChange = (newStatus: AppointmentExamStatus) => {
    // Flush any pending debounced save before changing status
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const data: UpdateExamResultData = { status: newStatus };

    // Clear result fields when moving away from COMPLETED
    if (newStatus !== AppointmentExamStatus.COMPLETED) {
      data.result = null;
      data.resultDetails = null;
    }

    saveUpdate(data);
  };

  const handleResultChange = (newResult: AppointmentExamResult) => {
    saveUpdate({ result: newResult });
  };

  const handleResultDetailsChange = (value: string) => {
    setResultDetails(value);
    debouncedSave({ resultDetails: value || null });
  };

  const handleObservationsChange = (value: string) => {
    setObservations(value);
    debouncedSave({ observations: value || null });
  };

  // File upload
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'error' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const updated = await appointmentExamService.uploadFile(appointmentId, exam.id, file);
      onExamUpdated(updated);
      enqueueSnackbar('Arquivo enviado com sucesso', { variant: 'success' });
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar('Erro ao enviar arquivo', { variant: 'error' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // File download
  const handleDownload = () => {
    const url = appointmentExamService.getDownloadUrl(appointmentId, exam.id);
    window.open(url, '_blank');
  };

  // File delete
  const handleFileDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover o arquivo?')) return;

    setUploading(true);
    try {
      const updated = await appointmentExamService.deleteFile(appointmentId, exam.id);
      onExamUpdated(updated);
      enqueueSnackbar('Arquivo removido', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting file:', error);
      enqueueSnackbar('Erro ao remover arquivo', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // --------------------------------------------------
  // Derived state
  // --------------------------------------------------

  const isCompleted = exam.status === AppointmentExamStatus.COMPLETED;
  const isReferral = !!exam.referralId;
  const referralClinicName = exam.referral?.clinic?.tradeName || exam.referral?.clinic?.legalName;
  const examName = exam.occupationalExam?.examName ?? `Exame #${exam.occupationalExamId}`;
  const esocialCode = exam.occupationalExam?.esocialCode;
  const criteria = exam.occupationalExam?.criteria;
  const examPreparation = exam.occupationalExam?.examPreparation;
  const hasCriteriaOrPreparation = !!(criteria || examPreparation);

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header: Exam name + eSocial code + expand button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ScienceIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              {examName}
            </Typography>
            {esocialCode && (
              <Chip label={esocialCode} size="small" variant="outlined" color="default" />
            )}
            <Chip
              label={APPOINTMENT_EXAM_STATUS_LABELS[exam.status]}
              color={APPOINTMENT_EXAM_STATUS_COLORS[exam.status]}
              size="small"
            />
            {exam.result && (
              <Chip
                label={APPOINTMENT_EXAM_RESULT_LABELS[exam.result]}
                color={APPOINTMENT_EXAM_RESULT_COLORS[exam.result]}
                size="small"
                variant="outlined"
              />
            )}
            {isReferral && (
              <Chip
                label={`Encaminhado → ${referralClinicName || 'Clínica externa'}`}
                color="info"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}
              />
            )}
            {exam.applicationLevel && (
              <Chip
                label={APPLICATION_LEVEL_LABELS[exam.applicationLevel] ?? exam.applicationLevel}
                color={APPLICATION_LEVEL_COLORS[exam.applicationLevel] ?? 'default'}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            {exam.periodicityMonths && (
              <Typography variant="caption" color="text.secondary">
                (a cada {exam.periodicityMonths} meses)
              </Typography>
            )}
            {saving && <CircularProgress size={16} />}
          </Box>
          {hasCriteriaOrPreparation && (
            <Tooltip title={expanded ? 'Ocultar detalhes' : 'Ver critérios e preparo'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Expandable criteria/preparation section */}
        {hasCriteriaOrPreparation && (
          <Collapse in={expanded}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 2 }}>
              {criteria && (
                <Box sx={{ mb: examPreparation ? 1 : 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Critérios:
                  </Typography>
                  <Typography variant="body2">{criteria}</Typography>
                </Box>
              )}
              {examPreparation && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Preparo:
                  </Typography>
                  <Typography variant="body2">{examPreparation}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Referral notice */}
        {isReferral && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            Este exame foi encaminhado para <strong>{referralClinicName || 'clínica externa'}</strong>.
            O laudo será gerenciado pelo SESMT na tela de Laudos Terceirizados.
          </Alert>
        )}

        {/* Status select */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id={`exam-status-label-${exam.id}`}>Status</InputLabel>
          <Select
            labelId={`exam-status-label-${exam.id}`}
            value={exam.status}
            label="Status"
            disabled={isReferral}
            onChange={(e) => handleStatusChange(e.target.value as AppointmentExamStatus)}
          >
            {Object.values(AppointmentExamStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {APPOINTMENT_EXAM_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Result radio group - visible only when COMPLETED */}
        {isCompleted && (
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isReferral}>
            <FormLabel component="legend">Resultado</FormLabel>
            <RadioGroup
              row
              value={exam.result ?? ''}
              onChange={(e) => handleResultChange(e.target.value as AppointmentExamResult)}
            >
              {Object.values(AppointmentExamResult).map((result) => (
                <FormControlLabel
                  key={result}
                  value={result}
                  control={<Radio size="small" />}
                  label={
                    <Chip
                      label={APPOINTMENT_EXAM_RESULT_LABELS[result]}
                      color={APPOINTMENT_EXAM_RESULT_COLORS[result]}
                      size="small"
                      variant={exam.result === result ? 'filled' : 'outlined'}
                    />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}

        {/* Result details - visible only when COMPLETED */}
        {isCompleted && (
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Detalhes do Resultado"
            placeholder="Descreva os detalhes do resultado do exame..."
            value={resultDetails}
            onChange={(e) => handleResultDetailsChange(e.target.value)}
            disabled={isReferral}
            sx={{ mb: 2 }}
          />
        )}

        {/* Observations - always visible */}
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Observações"
          placeholder="Observações adicionais..."
          value={observations}
          onChange={(e) => handleObservationsChange(e.target.value)}
          disabled={isReferral}
          sx={{ mb: 2 }}
        />

        {/* File upload section — hidden for referral exams */}
        {!isReferral && (
        <Box sx={{ mb: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {exam.filePath ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CheckIcon color="success" fontSize="small" />
              <AttachFileIcon color="action" fontSize="small" />
              <Typography variant="body2" sx={{ mr: 0.5 }}>
                {exam.originalFileName ?? 'Arquivo anexado'}
              </Typography>
              {exam.fileSize && (
                <Typography variant="caption" color="text.secondary">
                  ({formatFileSize(exam.fileSize)})
                </Typography>
              )}
              <Tooltip title="Baixar arquivo">
                <IconButton size="small" color="primary" onClick={handleDownload}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <ActionIconButton
                acao={GERIR_ARQUIVO_DO_EXAME}
                rotulo="Remover arquivo"
                size="small"
                color="error"
                onClick={handleFileDelete}
                disabled={uploading}
              >
                {uploading ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
              </ActionIconButton>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Substituir
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Enviando...' : 'Anexar Arquivo'}
            </Button>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            PDF, JPEG, PNG ou GIF. Tamanho máximo: 10MB.
          </Typography>
        </Box>
        )}
      </CardContent>

      {/* Footer: completed by */}
      {isCompleted && exam.completedAt && (
        <CardActions sx={{ px: 2, pt: 0, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Concluído por {exam.completedBy?.name ?? 'N/A'} em {formatDate(exam.completedAt)}
          </Typography>
        </CardActions>
      )}
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ExamsTab: React.FC<ExamsTabProps> = ({
  appointmentId,
  appointmentExams,
  onExamsUpdate,
}) => {
  // Memoize the handler that replaces a single exam in the list
  const handleExamUpdated = useCallback(
    (updatedExam: AppointmentExam) => {
      const updatedList = appointmentExams.map((ex) =>
        ex.id === updatedExam.id ? updatedExam : ex,
      );
      onExamsUpdate(updatedList);
    },
    [appointmentExams, onExamsUpdate],
  );

  // Summary counts
  const summary = useMemo(() => {
    const total = appointmentExams.length;
    const completed = appointmentExams.filter(
      (e) => e.status === AppointmentExamStatus.COMPLETED,
    ).length;
    const pending = appointmentExams.filter(
      (e) => e.status === AppointmentExamStatus.PENDING,
    ).length;
    const inProgress = appointmentExams.filter(
      (e) => e.status === AppointmentExamStatus.IN_PROGRESS,
    ).length;
    return { total, completed, pending, inProgress };
  }, [appointmentExams]);

  if (appointmentExams.length === 0) {
    return (
      <Alert severity="info">
        Nenhum exame complementar associado a este agendamento.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Exames Complementares
        </Typography>
        <Chip label={`${summary.total} total`} size="small" variant="outlined" />
        {summary.completed > 0 && (
          <Chip
            label={`${summary.completed} concluído(s)`}
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {summary.inProgress > 0 && (
          <Chip
            label={`${summary.inProgress} em andamento`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        {summary.pending > 0 && (
          <Chip
            label={`${summary.pending} pendente(s)`}
            size="small"
            color="default"
            variant="outlined"
          />
        )}
      </Box>

      {/* Exam cards */}
      {appointmentExams.map((exam) => (
        <ExamCard
          key={exam.id}
          exam={exam}
          appointmentId={appointmentId}
          onExamUpdated={handleExamUpdated}
        />
      ))}
    </Box>
  );
};

export default ExamsTab;
